import type {
  BankStatementImportPreview,
  BankStatementImportRequest,
  BankStatementImportTransaction,
} from './types';
import { toDateInputValue } from '../grid/utils/dateFormatters';

export type ReviewedBankStatementTransaction = BankStatementImportTransaction & {
  CompanyName: string | null;
  BankName: string | null;
  CounterpartyCrmRelationId: number | null;
  CounterpartyName: string | null;
  TransactionDescription: string | null;
  InternalDescription: string | null;
  AmountEur: number | null;
  CrmIncomeExpenseCategoryId: number | null;
  CategoryName: string | null;
  CrmInvoiceId: number | null;
  InvoiceName: string | null;
  InvoiceSnapshotFileId: string | null;
};

export type ReviewedStatementFields = {
  statementDate: string;
  fromDate: string;
  toDate: string;
  openingBalance: number | null;
  closingBalance: number | null;
};

export type StatementOverrides = Record<string, ReviewedStatementFields>;

export type ReviewedStatementGroup = ReviewedStatementFields & {
  key: string;
  transactionDate: string;
  CompanyName: string | null;
  BankName: string | null;
  CrmRelationId: number | null;
  CrmRelationFinanceId: number | null;
  CrmCurrencyId: number | null;
  BankAccountName: string | null;
  AccountNumber: string | null;
  AccountCurrency: string | null;
  transactions: ReviewedBankStatementTransaction[];
};

const normalizeAccountNumber = (value: string | null | undefined) =>
  value?.replace(/[^a-z0-9]/gi, '').toUpperCase() ?? '';

export const groupKeyFor = (transaction: ReviewedBankStatementTransaction) =>
  [
    toDateInputValue(transaction.TransactionDate),
    transaction.CrmRelationId ?? '',
    transaction.CrmRelationFinanceId ??
      `account:${normalizeAccountNumber(transaction.AccountNumber)}`,
    transaction.CrmCurrencyId ?? '',
  ].join('|');

export const initializeReviewedTransactions = (
  preview: BankStatementImportPreview
): ReviewedBankStatementTransaction[] =>
  preview.Transactions.map((transaction) => {
    const transactionAccount = normalizeAccountNumber(transaction.AccountNumber);
    const statementAccount = normalizeAccountNumber(preview.Statement.AccountNumber);
    const canUseStatementAccount =
      !transactionAccount || (!!statementAccount && transactionAccount === statementAccount);

    return {
      ...transaction,
      TransactionDate: transaction.TransactionDate ?? null,
      IncomeExpenseType: transaction.IncomeExpenseType ?? null,
      CounterpartyRaw: transaction.CounterpartyRaw ?? null,
      Description: transaction.Description ?? null,
      Amount: Number.isFinite(transaction.Amount) ? transaction.Amount : null,
      CompanyName: canUseStatementAccount ? preview.Statement.CompanyName : null,
      BankName: preview.Statement.BankName,
      CrmRelationId:
        transaction.CrmRelationId ??
        (canUseStatementAccount ? preview.Statement.CrmRelationId : null),
      BankAccountName:
        transaction.BankAccountName ??
        (canUseStatementAccount ? preview.Statement.BankAccountName : null),
      CrmRelationFinanceId:
        transaction.CrmRelationFinanceId ??
        (canUseStatementAccount ? preview.Statement.CrmRelationFinanceId : null),
      CrmCurrencyId: transaction.CrmCurrencyId ?? preview.Statement.CrmCurrencyId,
      Currency: transaction.Currency ?? preview.Statement.AccountCurrency,
      CounterpartyCrmRelationId: null,
      CounterpartyName: null,
      TransactionDescription: transaction.Description ?? null,
      InternalDescription: null,
      AmountEur:
        (transaction as BankStatementImportTransaction & { AmountEur?: number | null }).AmountEur ??
        null,
      CrmIncomeExpenseCategoryId: null,
      CategoryName: null,
      CrmInvoiceId: null,
      InvoiceName: null,
      InvoiceSnapshotFileId: null,
    };
  });

export const groupReviewedTransactions = (
  transactions: ReviewedBankStatementTransaction[],
  statementOverrides: StatementOverrides,
  preview?: BankStatementImportPreview | null
): ReviewedStatementGroup[] => {
  const groups = new Map<string, ReviewedStatementGroup>();

  transactions.forEach((transaction) => {
    const key = groupKeyFor(transaction);
    const transactionDate = toDateInputValue(transaction.TransactionDate);
    const existing = groups.get(key);

    if (existing) {
      existing.transactions.push(transaction);
      return;
    }

    const overrides = statementOverrides[key];
    groups.set(key, {
      key,
      transactionDate,
      statementDate: overrides?.statementDate ?? toDateInputValue(preview?.Statement.StatementDate),
      fromDate: overrides?.fromDate ?? toDateInputValue(preview?.Statement.StatementDate),
      toDate: overrides?.toDate ?? toDateInputValue(preview?.Statement.StatementDate),
      openingBalance:
        overrides?.openingBalance !== undefined
          ? overrides.openingBalance
          : (preview?.Statement.OpeningBalance ?? null),
      closingBalance:
        overrides?.closingBalance !== undefined
          ? overrides.closingBalance
          : (preview?.Statement.ClosingBalance ?? null),
      CompanyName: transaction.CompanyName ?? preview?.Statement.CompanyName ?? null,
      BankName: transaction.BankName ?? preview?.Statement.BankName ?? null,
      CrmRelationId: transaction.CrmRelationId,
      CrmRelationFinanceId: transaction.CrmRelationFinanceId,
      CrmCurrencyId: transaction.CrmCurrencyId,
      BankAccountName: transaction.BankAccountName,
      AccountNumber: transaction.AccountNumber,
      AccountCurrency: transaction.Currency,
      transactions: [transaction],
    });
  });

  return Array.from(groups.values()).sort((a, b) =>
    [a.transactionDate, a.CrmRelationFinanceId ?? 0, a.CrmCurrencyId ?? 0]
      .join('|')
      .localeCompare(
        [b.transactionDate, b.CrmRelationFinanceId ?? 0, b.CrmCurrencyId ?? 0].join('|')
      )
  );
};

export const buildImportRequest = (
  preview: BankStatementImportPreview,
  groups: ReviewedStatementGroup[]
): BankStatementImportRequest => ({
  SourcePeriodFrom: preview.Statement.SourcePeriodFrom,
  SourcePeriodTo: preview.Statement.SourcePeriodTo,
  Statements: groups.map((group) => ({
    StatementDate: group.statementDate,
    SourcePeriodFrom: group.fromDate,
    SourcePeriodTo: group.toDate,
    CrmRelationId: group.CrmRelationId,
    CrmRelationFinanceId: group.CrmRelationFinanceId,
    CrmCurrencyId: group.CrmCurrencyId,
    OpeningBalance: group.openingBalance,
    ClosingBalance: group.closingBalance,
    InternalDescription: null,
    Transactions: group.transactions.map((transaction) => ({
      RowId: transaction.RowId,
      TransactionDate: transaction.TransactionDate,
      IncomeExpenseType: transaction.IncomeExpenseType,
      CounterpartyRaw: transaction.CounterpartyRaw,
      CounterpartyCrmRelationId: transaction.CounterpartyCrmRelationId,
      TransactionDescription: transaction.TransactionDescription,
      InternalDescription: transaction.InternalDescription,
      Amount: transaction.Amount,
      CrmCurrencyId: transaction.CrmCurrencyId,
      ExchangeRate: transaction.ExchangeRate,
      AmountEur: transaction.AmountEur,
      CrmIncomeExpenseCategoryId: transaction.CrmIncomeExpenseCategoryId,
      CrmInvoiceId: transaction.CrmInvoiceId,
      InvoiceSnapshotFileId: transaction.InvoiceSnapshotFileId,
      BankCategoryRaw: transaction.BankCategoryRaw,
      BalanceAfterTransaction: transaction.BalanceAfterTransaction,
    })),
  })),
});
